import React, { useEffect, useState, useMemo } from 'react';
import { connectMenu } from 'react-contextmenu';
import cm from 'classnames';

import { StoryBoardTracks } from 'services/storyboard.service';
import { ClipConfig } from 'services/clip.service';
import { useAppSelector, useAppDispatch } from 'store/store-hooks';

import { useLogger } from 'components/_hooks/use-logger';
import { useLocation, Path } from 'components/_hooks/use-router';
import { isNull, selectClipData } from 'components/app/_helpers/_app.helper';
import { useTimestampActions } from 'components/app/editor/timestamp/_mediator/use-timestamp-actions';
import { useClipsTimestamp } from 'components/app/editor/_mediator/editor-clips/use-clips-timestamp';
import { useClipsLinking } from 'components/app/editor/_mediator/editor-clips/use-clips-linking';
import { setActiveMenuClip, setConfigPanel } from 'components/app/editor/_mediator/editor.slice';
import { useRemoveOrUnpinClip } from 'components/app/editor/_mediator/editor-clips/use-remove-or-unpin-clip';
import { useSelectorCb } from 'components/app/selector/_mediator/use-selector-cb';
import { setPlayerSeekEnabled } from 'components/app/storyboard/_mediator/sb.slice';
import { useSbCb } from 'components/app/storyboard/_mediator/use-sb-cb';
import { ContextMenu, MenuTrigger, NestedMenu, MenuOption } from 'components/UI/context-menu/context-menu';

import styles from './_track-clip.module.scss';

interface MenuData {
  id: string;
  action: ClipConfig;
  ['data-clipId']: ClipId;
  ['data-track']?: StoryBoardTracks;
  ['data-cell']?: Index;
}
interface MenuModal {
  id: string;
  trigger: MenuData;
}
interface Props extends React.HTMLAttributes<HTMLDivElement> {
  clipId: ClipId;
  cell?: Index;
  track?: StoryBoardTracks;
}
const MENU_ID = 'clip-menu';

// C O M P O N E N T S

export const ClipMenuContainer = ({ clipId, track, cell, children, className, ...rest }: Props) => {
  return (
    <MenuTrigger
      key={clipId}
      id={MENU_ID}
      data-clipId={clipId}
      data-track={track}
      data-cell={cell}
      collect={(props) => props}
      attributes={{ className: cm(styles.ClipMenuContainer, className), style: { height: '100%' }, ...rest }}
      holdToDisplay={-1}
    >
      {children}
    </MenuTrigger>
  );
};

const MenuModal = (props: MenuModal) => {
  const { id, trigger } = props;
  const { activeClip, clipsMap, selectedClips, activeMenuClip } = useAppSelector((state) => state.editor);
  const { sbSelectClip } = useSbCb();
  const { srSelectClip } = useSelectorCb();
  const { path } = useLocation();
  const [isTimestampHovered, setIsTimestampHovered] = useState(false);
  const hotkeyPlayerTimeListener = useAppSelector((state) => state.timestamp.hotkeyPlayerTimeListener);
  const logger = useLogger('MenuModal');
  const removeOrUnpinClip = useRemoveOrUnpinClip();
  const dispatch = useAppDispatch();

  const payload = useMemo(() => {
    if (!trigger) return;
    return {
      clipId: trigger['data-clipId'],
      cell: trigger['data-cell'],
      track: trigger['data-track'],
    };
  }, [trigger]);

  const handleTimestamp = useClipsTimestamp(payload?.clipId);
  const getTsActions = useTimestampActions();

  const clipData = useMemo(() => {
    if (!clipsMap || !payload?.clipId) return;
    const clipData = selectClipData(payload.clipId, clipsMap);
    if (clipData) return clipData;
    logger.error('clipData not found');
  }, [clipsMap, payload?.clipId]);

  const { linkClips, unlinkClips, linkIsDisabled, unlinkIsDisabled } = useClipsLinking(clipData);

  const handleRemove = () => {
    if (!payload?.clipId || !clipData) return;
    removeOrUnpinClip(payload.clipId, clipData);
  };

  const tsActions = useMemo(() => {
    if (!payload) return;
    if (isNull(hotkeyPlayerTimeListener)) return;
    return getTsActions(hotkeyPlayerTimeListener, payload.clipId);
  }, [hotkeyPlayerTimeListener, activeMenuClip]);

  useEffect(() => {
    if (!payload?.clipId) return;
    const { clipId, track, cell } = payload;
    dispatch(setActiveMenuClip(clipId));
    switch (path) {
      case Path.selector: {
        if (selectedClips.length === 0 && clipId !== activeClip) srSelectClip(clipId);
        break;
      }
      case Path.storyboard: {
        if (!cell || !track) return;
        if (clipId !== activeClip) sbSelectClip(clipId, cell, track);
        break;
      }
      default:
        break;
    }
  }, [payload?.clipId]);

  useEffect(() => {
    if (path === Path.selector) return;
    dispatch(setPlayerSeekEnabled(isTimestampHovered));
  }, [isTimestampHovered]);

  return (
    <ContextMenu
      id={id}
      onHide={() => {
        dispatch(setActiveMenuClip(null));
      }}
    >
      <NestedMenu title="Linking">
        <MenuOption disabled={linkIsDisabled()} onClick={linkClips}>
          Link
        </MenuOption>
        <MenuOption disabled={unlinkIsDisabled()} onClick={unlinkClips}>
          Unlink
        </MenuOption>
      </NestedMenu>
      <div onMouseEnter={() => setIsTimestampHovered(true)} onMouseLeave={() => setIsTimestampHovered(false)}>
        <NestedMenu title="Tags">
          <MenuOption disabled={!tsActions?.startTimeEnabled} onClick={() => handleTimestamp('start')}>
            Set Start Tag = {hotkeyPlayerTimeListener}
          </MenuOption>
          <MenuOption disabled={!tsActions?.endTimeEnabled} onClick={() => handleTimestamp('end')}>
            Set End Tag = {hotkeyPlayerTimeListener}
          </MenuOption>
          <MenuOption disabled={!tsActions?.splitEnabled} onClick={() => handleTimestamp('split')}>
            Split at {hotkeyPlayerTimeListener}
          </MenuOption>
          <MenuOption disabled={!tsActions?.removeStartTimeEnabled} onClick={() => handleTimestamp('removeStart')}>
            Remove Start Tag
          </MenuOption>
          <MenuOption disabled={!tsActions?.removeEndTimeEnabled} onClick={() => handleTimestamp('removeEnd')}>
            Remove End Tag
          </MenuOption>
        </NestedMenu>
      </div>
      {path !== Path.finalcuts && <MenuOption onClick={() => dispatch(setConfigPanel('clip'))}>Configure</MenuOption>}
      {path === Path.storyboard && payload?.clipId && <MenuOption onClick={handleRemove}>Remove</MenuOption>}
    </ContextMenu>
  );
};

export const TrackClipMenu = connectMenu(MENU_ID)(MenuModal);
